import { getCompanies } from '@/API/repositories/company';
import { getValidInvoicesReport } from '@/API/repositories/reports';
import { getPdfsByIds } from '@/API/repositories/storedDocument';
import { Colors } from '@/common/colors/colors';
import Card from '@/common/components/Card';
import ExportExcel from '@/common/components/ExportExcel';
import Loading from '@/common/components/Loading';
import SelectInput from '@/common/components/SelectInput';
import ToggleSwitch from '@/common/components/ToggleSwitch';
import ActionButton from '@/common/components/buttons/ActionButton';
import { formatDate } from '@/common/functions/dateFormater';
import { filterEntitiesByIds } from '@/common/functions/filterEntitiesByIds';
import { findDefaultMarketFromOptions } from '@/common/functions/findDefaultMarketFromOptions';
import { findPossibleProductFieldByMarket } from '@/common/functions/findPossibleProductFieldByMarket';
import { handleMapToOptions } from '@/common/functions/handleMapToOptions';
import { saveAs } from '@/common/functions/saveAs';
import { sortTicketsByInvoiceNumber } from '@/common/functions/sortTicketsByInvoiceNumber';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { useEffect, useMemo, useRef, useState } from 'react';
import { findCompanyNameById } from '../../../../../common/functions/findCompanyNameById';
import { Flex, RightFlex } from '../../ValidInvoicesReport.styled';
import { getMarketById } from '../../helpers/getMarketById';
import { Icon } from './ValidInvoicesReportSearchBar.styled';
import { getExcelData } from './helpers/getExcelData';
import { LAST_YEAR_MONTHS } from '@/common/constants/lastYearMonths';
import moment from 'moment';
import SendAccountingPopUp from '../sendAccountingPopUp/SendAccountingPopUp';

const JSZip = require('jszip');
const zip = new JSZip();

const ValidInvoicesReportSearchBar = ({
	tickets,
	setTickets,
	visibleTickets,
	setVisibleTickets,
}) => {
	const [selectedMarket, setSelectedMarket] = useState();
	const [selectedMonth, setSelectedMonth] = useState(LAST_YEAR_MONTHS[1]);
	const [companies, setCompanies] = useState();
	const [companiesOptions, setCompaniesOptions] = useState();
	const [selectedCompany, setSelectedCompany] = useState();
	const [isSendAccountingPopUp, setIsSendAccountingPopUp] = useState(false);

	const {
		options: { marketsOptions, queuesOptions },
		commonData: { markets, products },
	} = useCommonDataContext();
	const { addMessage } = useMessageQueueContext();
	const { makeRequest, hasUnfilledRequest } = useRequestsContext();

	const salesRef = useRef();
	const correctionRef = useRef();

	const handleSearch = async () => {
		if (!selectedMarket?.value) {
			return addMessage('Select market', 'error');
		}

		const payload = {};
		payload.market = selectedMarket.value;
		payload.company = selectedCompany.value;
		payload.fromDate = selectedMonth.value.startOf('month').toISOString();
		payload.toDate = selectedMonth.value.endOf('month').toISOString();

		const response = await makeRequest(
			getValidInvoicesReport.bind(null, payload),
		);

		if (!response.data) {
			return addMessage('Something went wrong', 'error');
		}

		if (response.data) {
			setTickets(() => response.data);
		}
	};

	const handleGetAllMonthFiles = async () => {
		const ids = visibleTickets.map(
			(ticket) => ticket.valid_invoice.stored_document,
		);

		const query = {};

		query.ids = ids;

		const response = await makeRequest(getPdfsByIds.bind(null, query));
		if (response.data) {
			const files = response.data;
			files.forEach((file) => {
				file.name = file.name.split('_')[1] || file.name;
			});

			files.forEach((file) => {
				zip.file(file.name, file.data.data);
			});

			const zipFileName = `${findCompanyNameById(
				companies,
				selectedCompany.value,
			)}_invoices_${
				getMarketById(selectedMarket.value, markets).name
			}_${formatDate(selectedMonth.value, 'MM_YYYY')}.zip`;

			zip.generateAsync({ type: 'blob' }).then((content) => {
				saveAs(content, zipFileName);
			});

			files.forEach((file) => {
				zip.remove(file.name);
			});
		}
	};

	const hansleSetVisibleTickets = () => {
		const corrections =
			tickets?.filter(
				(ticket) => ticket.valid_invoice.invoice_type === 'correction',
			) || [];
		const sales =
			tickets?.filter(
				(ticket) => ticket.valid_invoice.invoice_type === 'sales',
			) || [];

		setVisibleTickets(() => {
			const visible = [];

			if (salesRef.current?.checked) {
				visible.push(...sales);
			}

			if (correctionRef.current?.checked) {
				visible.push(...corrections);
			}

			return sortTicketsByInvoiceNumber(visible);
		});
	};

	const excelData = useMemo(() => {
		if (!!visibleTickets?.length) {
			const currency = visibleTickets[0]._order.currency;

			return getExcelData(visibleTickets, markets, currency);
		}
	}, [visibleTickets]);

	const loadData = async () => {
		const response = await makeRequest(getCompanies);

		if (response?.data) {
			setCompanies(() => response.data);
		}
	};

	const handleSetMarket = (market) => {
		const currentMarket =
			market || findDefaultMarketFromOptions(queuesOptions, marketsOptions);

		setSelectedMarket(currentMarket);
		handleMarketChange(currentMarket);
	};

	const handleMarketChange = (market) => {
		setSelectedCompany(() => null);
		setCompaniesOptions(() => null);

		if (market && companies) {
			const possibleCompanies = findPossibleProductFieldByMarket(
				queuesOptions,
				market.value,
				products,
				'company',
			);

			const uniqueCompaniesIds = Array.from(new Set(possibleCompanies));

			const filteredCompanies = filterEntitiesByIds(
				companies,
				uniqueCompaniesIds,
			);

			const newCompaniesOptions = handleMapToOptions(
				filteredCompanies,
				'name',
				'_id',
			);

			setCompaniesOptions(newCompaniesOptions);
			const company = newCompaniesOptions[0];

			handleSetCompany(company, market);
		}
	};

	const handleSetCompany = (company, market = selectedMarket) => {
		setSelectedCompany(company);
	};

	const getExcelFileName = () =>
		`${findCompanyNameById(
			companies,
			selectedCompany.value,
		)}_faktury_${formatDate(selectedMonth.value, 'MM_YYYY')}_${
			getMarketById(selectedMarket.value, markets).name
		}.csv`;

	useEffect(() => {
		loadData();
	}, []);

	useEffect(() => {
		handleSetMarket();
	}, [queuesOptions, marketsOptions, products, companies]);

	useEffect(() => {
		hansleSetVisibleTickets();
	}, [tickets]);

	return (
		<Card>
			{(hasUnfilledRequest(
				getValidInvoicesReport,
				getPdfsByIds,
				getCompanies,
			) ||
				!marketsOptions?.length) && <Loading />}
			<Flex>
				<div>
					<SelectInput
						name='Market'
						placeholder='Select market...'
						selectWidth={250}
						width={100}
						options={marketsOptions}
						setSelected={handleSetMarket}
						selected={selectedMarket}
						color={Colors.darkBlue}
					/>
					<SelectInput
						name='Company'
						placeholder='Select company...'
						selectWidth={250}
						width={100}
						options={companiesOptions}
						setSelected={handleSetCompany}
						selected={selectedCompany}
						color={Colors.darkBlue}
					/>
				</div>
				<div>
					<SelectInput
						name='Month'
						color={Colors.darkBlue}
						options={LAST_YEAR_MONTHS.filter((month) =>
							moment(month.value, 'MMMM YYYY').isAfter(moment('2024-09-30')),
						)}
						selected={selectedMonth}
						setSelected={setSelectedMonth}
						width={100}
					/>
					<RightFlex>
						<ToggleSwitch
							onChange={() => hansleSetVisibleTickets()}
							toggleRef={salesRef}
							text='Sales'
							width={130}
							gap='12px'
							checked
						/>
						<ToggleSwitch
							onChange={() => hansleSetVisibleTickets()}
							toggleRef={correctionRef}
							text='Correction'
							width={110}
							gap='12px'
							checked
						/>
					</RightFlex>
				</div>
			</Flex>
			<RightFlex>
				{!!visibleTickets?.length && (
					<>
						<ActionButton
							defaultText={'Wyślij do księgowości'}
							onClick={() => setIsSendAccountingPopUp(true)}
							style={{ marginRight: 'auto' }}
						/>
						<div>
							<Icon
								onClick={() => handleGetAllMonthFiles()}
								className='fa fa-file-zipper animation-scale'
							/>
						</div>
						<ExportExcel excelData={excelData} fileName={getExcelFileName()} />
					</>
				)}
				<ActionButton onClick={() => handleSearch()} defaultText='Search' />
			</RightFlex>

			{isSendAccountingPopUp && (
				<SendAccountingPopUp
					onClose={() => setIsSendAccountingPopUp(false)}
					month={selectedMonth.value}
					companyId={selectedCompany.value}
				/>
			)}
		</Card>
	);
};

export default ValidInvoicesReportSearchBar;
